import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";

import { CustomerEditService } from "../../services/customer-edit/customer-edit.service";
import { SectionAction } from "../shared/editable-section/editable-section.component";
import { DisplayItem } from "../shared/data-display/data-display.component";

import {
  CustomerInfoEditDialogComponent,
  CustomerInfoEditResult,
} from "../dialogs/customer-info-edit-dialog/customer-info-edit-dialog.component";


import {
  UserInfoDialogComponent,
  UserInfoDialogData,
} from "../dialogs/user-info-dialog/user-info-dialog.component";
import {
  AddRegionOrSiteDialogComponent,
  AddRegionSiteResult,
} from "../dialogs/addRegionOrSiteDialog/add-region-or-site-dialog.component";
import {
  AddUserDialogComponent,
  AddUserDialogData,
} from "../dialogs/addUserDialog/add-user-dialog.component";

interface CustomerSection {
  id: string;
  title: string;
  icon: string;
  editable: boolean;
  loading: boolean;
  error?: string;
}

@Component({
  selector: "app-customer-edit",
  templateUrl: "./customer-edit.component.html",
  styleUrls: ["./customer-edit.component.scss"],
})
export class CustomerEditComponent implements OnInit, OnDestroy {
  customerId: number | null = null;
  customer: any = null;
  isLoading = false;
  error = "";

  private _cachedDisplayItems: { [key: string]: DisplayItem[] } = {};
  private _cachedSubtitles: {
    [key: string]: { value: string; _timestamp: number };
  } = {};
  private _cachedActions: { [key: string]: SectionAction[] } = {};
  private _lastCustomerUpdate: number = 0;

  sections: CustomerSection[] = [
    {
      id: "basic-info",
      title: "Customer Information",
      icon: "account_circle",
      editable: true,
      loading: false,
    },
    {
      id: "regions",
      title: "Regions",
      icon: "location_on",
      editable: true,
      loading: false,
    },
    {
      id: "sites",
      title: "Sites",
      icon: "business",
      editable: true,
      loading: false,
    },
    {
      id: "users",
      title: "Users",
      icon: "people",
      editable: true,
      loading: false,
    },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private customerEditService: CustomerEditService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.customerId = params["id"] ? parseInt(params["id"], 10) : null;

      if (this.customerId) {
        this.loadCustomerData();
      } else {
        this.error = "Invalid customer ID";
      }
    });
  }

  private loadCustomerData(): void {
    if (!this.customerId) return;

    this.isLoading = true;
    this.error = "";

    this.customerEditService
      .loadCustomerData(this.customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.customer = data;
          this._lastCustomerUpdate = Date.now();
          this.clearCache();
          this.isLoading = false;
        },
        error: (error) => {
          console.error("Error loading customer:", error);
          this.error = "Failed to load customer data";
          this.isLoading = false;
          this.toastr.error("Failed to load customer data", "Error");
        },
      });
  }

  getBasicInfoActions(): SectionAction[] {
    return this.getCachedActions("basic-info", () => [
      { id: "basic-info", label: "Edit Information", icon: "edit", primary: true },
    ]);
  }

  getAllUsersActions(): SectionAction[] {
    return this.getCachedActions("all-users", () => [
      {
        id: "add-admin-user",
        label: "Add Admin",
        icon: "person_add",
        primary: true,
      },
      {
        id: "add-operator-user",
        label: "Add Operator",
        icon: "person_add_alt",
        primary: true,
      },
    ]);
  }

  getRegionsSubtitle(): string {
    return this.getCachedSubtitle("regions", () => {
      const regions = this.getSectionData("regions") || [];
      return `${regions.length} region${regions.length !== 1 ? "s" : ""}`;
    });
  }

  getSitesSubtitle(): string {
    return this.getCachedSubtitle("sites", () => {
      const sites = this.getSectionData("sites") || [];
      return `${sites.length} site${sites.length !== 1 ? "s" : ""}`;
    });
  }

  getAdminUsersSubtitle(): string {
    return this.getCachedSubtitle("admin-users", () => {
      const users = this.getAdminUsers();
      return `${users.length} admin user${users.length !== 1 ? "s" : ""}`;
    });
  }

  getOperatorUsersSubtitle(): string {
    return this.getCachedSubtitle("operator-users", () => {
      const users = this.getOperatorUsers();
      return `${users.length} operator user${users.length !== 1 ? "s" : ""}`;
    });
  }

  // Combined Users Subtitle
  getAllUsersSubtitle(): string {
    return this.getCachedSubtitle("all-users", () => {
      const adminUsers = this.getAdminUsers();
      const operatorUsers = this.getOperatorUsers();
      const totalUsers = adminUsers.length + operatorUsers.length;
      return `${totalUsers} total user${totalUsers !== 1 ? "s" : ""} (${
        adminUsers.length
      } admin, ${operatorUsers.length} operator)`;
    });
  }

  getBasicInfoDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("basic-info", () => {
      const basicData = this.getSectionData("basic-info");
      if (!basicData) return [];

      return [
        {
          label: "Customer Name",
          value: (basicData.customer && basicData.customer.name) || "",
          type: "text",
        },
        {
          label: "Email",
          value: (basicData.customer && basicData.customer.email) || "",
          type: "email",
        },
        {
          label: "Contact Number",
          value: (basicData.customer && basicData.customer.contactNo) || "",
          type: "text",
        },
        {
          label: "Pan Number",
          value: (basicData.customer && basicData.customer.panNo) || "",
          type: "text",
        },
        {
          label: "GST Number",
          value: (basicData.customer && basicData.customer.gstNo) || "",
          type: "text",
        },
      ];
    });
  }

  getRegionsDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("regions", () => {
      const regions = this.getSectionData("regions") || [];
      return regions.map((region: any) => ({
        label: region.name,
        value: region.name,
        icon: "location_on",
      }));
    });
  }

  getSitesDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("sites", () => {
      const sites = this.getSectionData("sites") || [];
      return sites.map((site: any) => ({
        label: site.name,
        value: site.name,
        icon: "business",
        metadata:
          (site.region && site.region.name) || site.regionName
            ? `Region: ${(site.region && site.region.name) || site.regionName}`
            : "",
      }));
    });
  }

  getAdminUsersDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("admin-users", () => {
      const users = this.getAdminUsers();
      return users.map((user: any) => ({
        label: (user.userDetails && user.userDetails.name) || user.username,
        value: (user.userDetails && user.userDetails.name) || user.username,
        icon: "person",
        metadata: (user.userDetails && user.userDetails.email) || "",
      }));
    });
  }

  getOperatorUsersDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("operator-users", () => {
      const users = this.getOperatorUsers();
      return users.map((user: any) => ({
        label: (user.userDetails && user.userDetails.name) || user.username,
        value: (user.userDetails && user.userDetails.name) || user.username,
        icon: "person",
        metadata: (user.userDetails && user.userDetails.email) || "",
      }));
    });
  }

  getAllUsersDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("all-users", () => {
      const adminUsers = this.getAdminUsers();
      const operatorUsers = this.getOperatorUsers();

      const allUsers = [
        ...adminUsers.map((user: any) => ({
          ...user,
          userType: "admin",
          userTypeLabel: "Admin",
        })),
        ...operatorUsers.map((user: any) => ({
          ...user,
          userType: "operator",
          userTypeLabel: "Operator",
        })),
      ];

      return allUsers.map((user: any) => ({
        label: (user && user.name) || user.username || "Unknown User",
        value: user.username || "N/A",
        type: "user-row",
        metadata: {
          email: (user && user.email) || "--",
          id: user.id ,
          userType: user.userTypeLabel,
          userTypeClass: user.userType,
          roles: user.roles
            ? user.roles.map((role: any) => role.name).join(", ")
            : "--",
          regions: user.regions
            ? user.regions.map((region: any) => ({name:region.name, id:region.id}))
            : [],
          sites: user.sites
            ? user.sites.map((site: any) => ({name:site.name, id:site.id, regionId: site.regionId, regionName: site.regionName}))
            : [],
          mobile: (user.mobile && user.mobile) || "--",
        },
      }));
    });
  }

  getRegionsSitesDisplayItems(): DisplayItem[] {
    return this.getCachedDisplayItems("regions-sites", () => {
      const regions = this.getSectionData("regions") || [];
      const sites = this.getSectionData("sites") || [];

      return regions.map((region: any) => {
        // Find sites that belong to this region
        const regionSites = sites.filter((site: any) => {
          const siteRegionName =
            (site.region && site.region.name) || site.regionName;
          return siteRegionName === region.name;
        });

        return {
          id: region.id || region.name,
          label: region.name,
          value: region.name,
          icon: "location_on",
          metadata: {
            sites: regionSites.map((site: any) => ({
              name: site.name,
              label: site.name,
              address: site.address || "",
              id: site.id,
            })),
          },
        };
      });
    });
  }

  getRegionsSitesSubtitle(): string {
    return this.getCachedSubtitle("regions-sites", () => {
      const regions = this.getSectionData("regions") || [];
      const sites = this.getSectionData("sites") || [];
      return `${regions.length} regions, ${sites.length} sites`;
    });
  }

  getRegionsSitesActions(): SectionAction[] {
    return this.getCachedActions("regions-sites", () => [
      {
        id: "add-region",
        label: "Add Region",
        icon: "add_location",
        primary: true,
      },
      {
        id: "add-site",
        label: "Add Site",
        icon: "add_business",
        primary: true,
      },
    ]);
  }

  getSectionData(sectionId: string): any {
    if (!this.customer) return null;

    switch (sectionId) {
      case "basic-info":
        return {
          customer: this.customer.customer,
        };
      case "regions":
        return this.customer.regions || [];
      case "sites":
        return this.customer.sites || [];
      case "users":
        return [...this.getAdminUsers(), ...this.getOperatorUsers()];
      default:
        return null;
    }
  }

  private getAdminUsers(): any[] {
    if (!this.customer || !this.customer.users) return [];
    return this.customer.users.filter((user: any) =>
      user.roles.some((role: any) => role.name.toLowerCase() === "admin")
    );
  }

  private getOperatorUsers(): any[] {
    if (!this.customer || !this.customer.users) return [];
    return this.customer.users.filter((user: any) =>
      user.roles.some((role: any) => role.name.toLowerCase() === "user")
    );
  }

  onEditSection(sectionId: string): void {
    switch (sectionId) {
      case "basic-info":
        this.openCustomerInfoEditDialog();
        break;
      case "add-region":
        this.openAddRegionSiteDialog("ADD_REGION");
        break;
      case "add-site":
        this.openAddRegionSiteDialog("ADD_SITE");
        break;
      case "add-admin-user":
        this.openAddUserDialog("ADD_ADMIN");
        break;
      case "add-operator-user":
        this.openAddUserDialog("ADD_OPERATOR");
        break;
      default:
        console.log("Unknown section:", sectionId);
    }
  }

  private openCustomerInfoEditDialog(): void {
    const dialogRef = this.dialog.open(CustomerInfoEditDialogComponent, {
      width: "600px",
      disableClose: true,
      data: {
        customer: this.customer.customer,
      },
    });

    dialogRef.afterClosed().subscribe((result: CustomerInfoEditResult) => {
      if (result) {
        this.updateCustomerInfo(result);
      }
    });
  }

  private updateCustomerInfo(result: CustomerInfoEditResult): void {
    const section = this.sections.find((s) => s.id === "basic-info");
    if (section) {
      section.loading = true;
    }

    this.customerEditService
      .updateCustomerInfo(this.customerId!, result)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedCustomer) => {
          this.customer = updatedCustomer;
          this._lastCustomerUpdate = Date.now();
          this.clearCache();
          this.refreshCustomerData();
          this.toastr.success("Customer information updated successfully");
          if (section) {
            section.loading = false;
          }
        },
        error: (error) => {
          console.error("Error updating customer info:", error);
          this.toastr.error("Failed to update customer information");
          if (section) {
            section.loading = false;
            section.error = "Failed to update";
          }
        },
      });
  }

  private openAddRegionSiteDialog(formType: string): void {
    const dialogData: any = {
      formType: formType,
      customerId: this.customerId,
    };

    if (formType === "ADD_SITE" && this.customer && this.customer.regions) {
      dialogData.availableRegions = this.customer.regions;
    }

    const dialogRef = this.dialog.open(AddRegionOrSiteDialogComponent, {
      width: "600px",
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: AddRegionSiteResult) => {
      if (result) {
        this.handleAddRegionSiteResult(result);
      }
    });
  }

  private handleAddRegionSiteResult(result: AddRegionSiteResult): void {
    this.isLoading = true;

    let data = {
      customerId: result.customerId,
      name: result.name,
    };
    if (result.type === "region") {
      this.customerEditService
        .addRegion(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isLoading = false;
            this._lastCustomerUpdate = Date.now();
            this.clearCache();
            this.refreshCustomerData();
            this.toastr.success(`Region added successfully`);
          },
          error: (error) => {
            console.error("Error reloading customer data:", error);
            this.toastr.error("Added successfully but failed to refresh data");
            this.isLoading = false;
          },
        });
    } else if (result.type === "site") {
      let data = {
        customerId: result.customerId,
        name: result.name,
        regionId: result.regionId,
      };
      this.customerEditService
        .addSite(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isLoading = false;
            this._lastCustomerUpdate = Date.now();
            this.clearCache();
            this.toastr.success(`Site added successfully`);

            this.refreshCustomerData();
          },
          error: (error) => {
            console.error("Error reloading customer data:", error);
            this.toastr.error("Added successfully but failed to refresh data");
            this.isLoading = false;
          },
        });
    }
  }

  private openAddUserDialog(userType: string): void {

    let dialogUserType: 'admin' | 'operator';
    if (userType === 'ADD_ADMIN') {
      dialogUserType = 'admin';
    } else if (userType === 'ADD_OPERATOR') {
      dialogUserType = 'operator';
    } else {
      dialogUserType = 'operator';
    }

    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        userType: dialogUserType,
        mode: 'create',
        regions: this.customer && this.customer.regions ? this.customer.regions : [],
        sites: this.customer && this.customer.sites ? this.customer.sites : []
      } as AddUserDialogData
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.isLoading = true;
        
        if (dialogUserType === 'admin') {
          let regionNames: string[] = [];
          if (result.regionId && this.customer && this.customer.regions) {
            const selectedRegion = this.customer.regions.find(region => region.id == result.regionId);
            if (selectedRegion) {
              regionNames = [selectedRegion.name];
            }
          }
          
          const adminPayload = {
            customerId: this.customer && this.customer.customer ? this.customer.customer.id : null,
            username: result.username,
            password: result.password,
            userDetails: {
              name: result.fullName,
              email: result.email,
              mobile: result.mobile || '',
              emp_code: result.emp_code || ''
            },
            regionNames: regionNames
          };
          
          this.customerEditService.addAdminUser(adminPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.toastr.success('Admin added successfully');
                this.refreshCustomerData();
                this.isLoading = false;
              },
              error: (error) => {
                this.toastr.error('Failed to add admin');
                this.isLoading = false;
              }
            });
        } else {
          // Prepare operator user data with region and site selection
          let regionNames: string[] = [];
          let siteNames: string[] = [];
          
          if (result.operatorRegionId && this.customer && this.customer.regions) {
            const selectedRegion = this.customer.regions.find(region => region.id == result.operatorRegionId);
            if (selectedRegion) {
              regionNames = [selectedRegion.name];
            }
          }
          
          if (result.operatorSiteIds && this.customer && this.customer.sites) {
            result.operatorSiteIds.forEach((siteId: string) => {
              const selectedSite = this.customer.sites.find((site: any) => site.id == siteId);
              if (selectedSite) {
                siteNames.push(selectedSite.name);
              }
            });
          }
          
          const operatorPayload = {
            customerId: this.customerId,
            username: result.username,
            password: result.password,
            userDetails: {
              name: result.fullName,
              email: result.email,
              mobile: result.mobile || '',
              emp_code: result.emp_code || ''
            },
            regionNames: regionNames,
            siteNames: siteNames
          };
          
          this.customerEditService.addOperatorUser(operatorPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.toastr.success('Operator added successfully');
                this.refreshCustomerData();
                this.isLoading = false;
              },
              error: (error) => {
                this.toastr.error('Failed to add operator');
                this.isLoading = false;
              }
            });
        }
      }
    });
  }


  onBackToList(): void {
    this.router.navigate(["/landing/customer-list"]);
  }

  refreshCustomerData(): void {
    this.loadCustomerData();
  }

  onUserInfo(user: any): void {
    const dialogRef = this.dialog.open(UserInfoDialogComponent, {
      width: "700px",
      disableClose: false,
      data: {
        user: user
      } as UserInfoDialogData
    });

    dialogRef.afterClosed().subscribe(() => {
      // Dialog closed, no action needed
    });
  }

  onUserEdit(user: any): void {
    // Determine user type from metadata or roles
    const isAdmin = (user.metadata && user.metadata.userTypeClass === 'admin') || 
                    (user.metadata && user.metadata.userType === 'Admin') ||
                    (user.roles && user.roles.some((role: any) => role.name.toLowerCase() === 'admin'));
    
    const userType = isAdmin ? 'admin' : 'operator';
    
    // Prepare user data for editing
    const userData = {
      id: (user.metadata && user.metadata.id),
      fullName: user.label || user.name || '',
      username: user.value || user.username || '',
      email: (user.metadata && user.metadata.email) || '',
      mobile: (user.metadata && user.metadata.mobile) || '',
      emp_code: user.emp_code || '',
      userDetails: user.userDetails || null,
      roles: user.roles || [],
      regions:(user.metadata && user.metadata.regions) || [],
      sites: (user.metadata && user.metadata.sites) || []
    };

    this.openEditUserDialog(userType, userData);
  }

  private openEditUserDialog(userType: 'admin' | 'operator', userData: any): void {

    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        userType: userType,
        mode: 'edit',
        userData: userData,
        regions: this.customer && this.customer.regions ? this.customer.regions : [],
        sites: this.customer && this.customer.sites ? this.customer.sites : []
      } as AddUserDialogData
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        // Handle user update
        this.isLoading = true;
        const payload = {
          id: userData.id,
          customerId: this.customerId,
          ...result,
          siteIds: result.operatorSiteIds ? result.operatorSiteIds : [],
          regionIds: result.userType === 'admin' ? (result.regionId ? [result.regionId] : []) : (result.operatorRegionId ? [result.operatorRegionId] : []),
          userDetails: {
            name: result.fullName? result.fullName : null,
            email: result.email ? result.email : null,
            mobile: result.mobile ? result.mobile : null,
            emp_code: result.emp_code ? result.emp_code : null
          }
        };

        if (userType === 'admin') {
          this.customerEditService.updateAdminUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.toastr.success('Admin updated successfully');
                this.refreshCustomerData();
                this.isLoading = false;
              },
              error: (error) => {
                console.error('Error updating admin:', error);
                this.toastr.error('Failed to update admin');
                this.isLoading = false;
              }
            });
        } else {
          this.customerEditService.updateOperatorUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.toastr.success('Operator updated successfully');
                this.refreshCustomerData();
                this.isLoading = false;
              },
              error: (error) => {
                console.error('Error updating operator:', error);
                this.toastr.error('Failed to update operator');
                this.isLoading = false;
              }
            });
        }
      }
    });
  }

  // Cache Helper Methods
  private clearCache(): void {
    this._cachedDisplayItems = {};
    this._cachedSubtitles = {};
    this._cachedActions = {};
  }

  private getCachedDisplayItems(
    key: string,
    factory: () => DisplayItem[]
  ): DisplayItem[] {
    if (
      !this._cachedDisplayItems[key] ||
      this._lastCustomerUpdate >
        (this._cachedDisplayItems[key] as any)._timestamp
    ) {
      const items = factory();
      this._cachedDisplayItems[key] = items;
      (this._cachedDisplayItems[key] as any)._timestamp = Date.now();
    }
    return this._cachedDisplayItems[key];
  }

  private getCachedSubtitle(key: string, factory: () => string): string {
    if (
      !this._cachedSubtitles[key] ||
      this._lastCustomerUpdate > this._cachedSubtitles[key]._timestamp
    ) {
      const subtitle = factory();
      this._cachedSubtitles[key] = {
        value: subtitle,
        _timestamp: Date.now(),
      };
    }
    return this._cachedSubtitles[key].value;
  }

  private getCachedActions(
    key: string,
    factory: () => SectionAction[]
  ): SectionAction[] {
    if (
      !this._cachedActions[key] ||
      this._lastCustomerUpdate > (this._cachedActions[key] as any)._timestamp
    ) {
      const actions = factory();
      this._cachedActions[key] = actions;
      (this._cachedActions[key] as any)._timestamp = Date.now();
    }
    return this._cachedActions[key];
  }
}
